import { FileText, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Button, ModalContentWrapper, Spinner } from '@/components/ui';
import {
  useCreateGuarantor,
  useGuarantorSummary,
  useGuarantors,
  useReleaseGuarantor,
} from '@/hooks/use-guarantors';
import { cn } from '@/lib/cn';
import { extractErrorMessage, handleMutationError } from '@/lib/error-handling';
import { fmtCurrency } from '@/lib/format';
import { sanitizeAbn, sanitizePhone } from '@/lib/input-restrictions';
import { createGuarantorSchema, validateWithSchema } from '@/lib/schemas';
import { toast } from '@/stores/toast-store';

interface Props {
  loanId: string;
}

interface GuarantorFormState {
  name: string;
  entityType: 'INDIVIDUAL' | 'COMPANY' | 'TRUST';
  abn: string;
  email: string;
  phone: string;
  guaranteeType: 'PERSONAL' | 'CORPORATE' | 'DIRECTOR' | 'THIRD_PARTY' | 'LIMITED_RECOURSE';
  limitOfLiability: string;
  guaranteeDate: string;
  expiryDate: string;
  notes: string;
}

const GUARANTEE_TYPE_LABELS: Record<GuarantorFormState['guaranteeType'], string> = {
  PERSONAL: 'Personal',
  CORPORATE: 'Corporate',
  DIRECTOR: 'Director',
  THIRD_PARTY: 'Third Party',
  LIMITED_RECOURSE: 'Limited Recourse',
};

const initialFormData: GuarantorFormState = {
  name: '',
  entityType: 'INDIVIDUAL',
  abn: '',
  email: '',
  phone: '',
  guaranteeType: 'PERSONAL',
  limitOfLiability: '',
  guaranteeDate: '',
  expiryDate: '',
  notes: '',
};

export function GuarantorTab({ loanId }: Props) {
  const { data: guarantors, isLoading } = useGuarantors(loanId);
  const { data: summary } = useGuarantorSummary(loanId);
  const createGuarantor = useCreateGuarantor();
  const releaseGuarantor = useReleaseGuarantor();
  const [showModal, setShowModal] = useState(false);
  const [releaseId, setReleaseId] = useState<string | null>(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [formData, setFormData] = useState<GuarantorFormState>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');

  function resetForm() {
    setFormData(initialFormData);
    setErrors({});
    setFormError('');
  }

  function handleCloseModal() {
    resetForm();
    setShowModal(false);
  }

  function clearError(path: string) {
    setErrors(current => {
      if (!current[path]) {
        return current;
      }

      const next = { ...current };
      delete next[path];
      return next;
    });
  }

  function updateField<K extends keyof GuarantorFormState>(field: K, value: GuarantorFormState[K]) {
    setFormData(current => ({ ...current, [field]: value }));
    clearError(field);
    setFormError('');
  }

  function getFieldError(path: string) {
    return errors[path];
  }

  const inputClass =
    'w-full rounded-md border px-3 py-2 text-sm placeholder:text-gray-400 focus:ring-1 outline-none';

  function getInputClass(path: string) {
    return cn(
      inputClass,
      getFieldError(path)
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
    );
  }

  function handleAddGuarantor(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    const validation = validateWithSchema(createGuarantorSchema, {
      loanId,
      ...formData,
    });

    if (!validation.success) {
      setErrors(validation.errors);
      const firstError = Object.values(validation.errors)[0];
      if (firstError) {
        toast.error(firstError);
      }
      return;
    }

    setErrors({});

    createGuarantor.mutate(
      {
        loanId,
        name: validation.data.name,
        entityType: validation.data.entityType,
        abn: validation.data.abn,
        email: validation.data.email,
        phone: validation.data.phone,
        guaranteeType: validation.data.guaranteeType,
        limitOfLiability: String(validation.data.limitOfLiability),
        guaranteeDate: validation.data.guaranteeDate,
        expiryDate: validation.data.expiryDate,
        notes: validation.data.notes,
      },
      {
        onSuccess: () => {
          toast.success('Guarantor added');
          handleCloseModal();
        },
        onError: (err: any) => {
          setFormError(extractErrorMessage(err));
          handleMutationError(err, 'Failed to add guarantor');
        },
      },
    );
  }

  function handleRelease(id: string) {
    if (!releaseReason.trim()) {
      toast.error('Please provide a release reason');
      return;
    }

    releaseGuarantor.mutate(
      { id, loanId, reason: releaseReason.trim() },
      {
        onSuccess: () => {
          toast.success('Guarantor released');
          setReleaseId(null);
          setReleaseReason('');
        },
        onError: (err: any) => handleMutationError(err, 'Failed to release guarantor'),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2 text-gray-400" />
        <span className="text-gray-400">Loading guarantors…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total Guarantors
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalGuarantors}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Total Liability
            </p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {fmtCurrency(summary.totalLiability)}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Active</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{summary.activeCount}</p>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Released</p>
            <p className="text-2xl font-bold text-gray-500 mt-1">{summary.releasedCount}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Guarantors</h3>
        <Button
          variant="primary"
          onClick={() => setShowModal(true)}
          className="flex w-auto items-center gap-1.5 px-3 py-2"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Guarantor
        </Button>
      </div>

      {!guarantors || guarantors.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No guarantors added yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Guarantee Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Limit of Liability
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Expiry Date
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {guarantors.map(g => (
                <tr key={g.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{g.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs font-medium">
                      {g.entityType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {GUARANTEE_TYPE_LABELS[g.guaranteeType as keyof typeof GUARANTEE_TYPE_LABELS] ??
                      g.guaranteeType}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">
                    {fmtCurrency(g.limitOfLiability)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={cn(
                        'px-2 py-1 rounded-full text-xs font-medium',
                        g.status === 'ACTIVE' && 'bg-green-100 text-green-800',
                        g.status === 'RELEASED' && 'bg-gray-100 text-gray-800',
                        g.status !== 'ACTIVE' &&
                          g.status !== 'RELEASED' &&
                          'bg-orange-100 text-orange-800',
                      )}
                    >
                      {g.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {g.expiryDate ? new Date(g.expiryDate).toLocaleDateString('en-AU') : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {g.status === 'ACTIVE' && (
                      <button
                        onClick={() => setReleaseId(g.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Release
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ModalContentWrapper open={showModal} onClose={handleCloseModal} title="Add Guarantor">
        <form onSubmit={handleAddGuarantor} className="space-y-4">
          {formError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => updateField('name', e.target.value)}
              placeholder="Guarantor name"
              className={getInputClass('name')}
            />
            {getFieldError('name') && (
              <p className="mt-1 text-xs text-red-600">{getFieldError('name')}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Entity Type *</label>
              <select
                value={formData.entityType}
                onChange={e =>
                  updateField('entityType', e.target.value as GuarantorFormState['entityType'])
                }
                className={getInputClass('entityType')}
              >
                <option value="INDIVIDUAL">Individual</option>
                <option value="COMPANY">Company</option>
                <option value="TRUST">Trust</option>
              </select>
              {getFieldError('entityType') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('entityType')}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                ABN {formData.entityType !== 'INDIVIDUAL' ? '*' : ''}
              </label>
              <input
                type="text"
                value={formData.abn}
                onChange={e => updateField('abn', sanitizeAbn(e.target.value))}
                placeholder="11 digits"
                inputMode="numeric"
                autoComplete="off"
                maxLength={14}
                pattern="[0-9 ]*"
                className={getInputClass('abn')}
              />
              {getFieldError('abn') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('abn')}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => updateField('email', e.target.value)}
                placeholder="email@example.com"
                className={getInputClass('email')}
              />
              {getFieldError('email') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('email')}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={e => updateField('phone', sanitizePhone(e.target.value))}
                placeholder="+61 4xx xxx xxx"
                inputMode="tel"
                autoComplete="tel"
                maxLength={20}
                className={getInputClass('phone')}
              />
              {getFieldError('phone') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('phone')}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Guarantee Type *
              </label>
              <select
                value={formData.guaranteeType}
                onChange={e =>
                  updateField(
                    'guaranteeType',
                    e.target.value as GuarantorFormState['guaranteeType'],
                  )
                }
                className={getInputClass('guaranteeType')}
              >
                <option value="PERSONAL">Personal</option>
                <option value="CORPORATE">Corporate</option>
                <option value="DIRECTOR">Director</option>
                <option value="THIRD_PARTY">Third Party</option>
                <option value="LIMITED_RECOURSE">Limited Recourse</option>
              </select>
              {getFieldError('guaranteeType') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('guaranteeType')}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Limit of Liability (AUD) *
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={formData.limitOfLiability}
                onChange={e => updateField('limitOfLiability', e.target.value)}
                placeholder="0.00"
                className={getInputClass('limitOfLiability')}
              />
              {getFieldError('limitOfLiability') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('limitOfLiability')}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Guarantee Date</label>
              <input
                type="date"
                value={formData.guaranteeDate}
                onChange={e => updateField('guaranteeDate', e.target.value)}
                className={getInputClass('guaranteeDate')}
              />
              {getFieldError('guaranteeDate') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('guaranteeDate')}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Expiry Date</label>
              <input
                type="date"
                value={formData.expiryDate}
                onChange={e => updateField('expiryDate', e.target.value)}
                className={getInputClass('expiryDate')}
              />
              {getFieldError('expiryDate') && (
                <p className="mt-1 text-xs text-red-600">{getFieldError('expiryDate')}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => updateField('notes', e.target.value)}
              placeholder="Additional notes"
              rows={4}
              className={getInputClass('notes')}
            />
            {getFieldError('notes') && (
              <p className="mt-1 text-xs text-red-600">{getFieldError('notes')}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseModal}
              disabled={createGuarantor.isPending}
              className="w-auto px-4"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createGuarantor.isPending}
              disabled={createGuarantor.isPending}
              className="w-auto min-w-[160px] px-5"
            >
              Add Guarantor
            </Button>
          </div>
        </form>
      </ModalContentWrapper>

      <ModalContentWrapper
        open={!!releaseId}
        onClose={() => setReleaseId(null)}
        title="Release Guarantor"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Release Reason *</label>
            <textarea
              value={releaseReason}
              onChange={e => setReleaseReason(e.target.value)}
              placeholder="Reason for releasing this guarantor…"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
            />
          </div>

          <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setReleaseId(null)}
              className="w-auto px-4"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => releaseId && handleRelease(releaseId)}
              loading={releaseGuarantor.isPending}
              disabled={releaseGuarantor.isPending}
              className="w-auto px-4 bg-red-600 hover:bg-red-700 disabled:bg-red-200"
            >
              Release Guarantor
            </Button>
          </div>
        </div>
      </ModalContentWrapper>
    </div>
  );
}
